/**
 * Brand identity — owned by the code, deliberately **not** by the database.
 *
 * The site name ends up in the document title, canonical URLs, structured data,
 * the sitemap, the RSS feed and every social card. Those have to be identical
 * for a crawler fetching raw HTML and for a reader running the app, and they
 * have to be knowable before any network request resolves — a value fetched
 * from `site_settings` is none of those things. So the name is a constant here
 * and there is no admin control for it (see `SiteSettingsForm`).
 *
 * Editable-by-admin copy (tagline, about text, contact email) still lives in
 * `site_settings`; the values below are the defaults those fall back to.
 *
 * This module must stay dependency-free and side-effect-free: the Cloudflare
 * Pages Functions under `apps/web/functions/` import it directly at the edge,
 * where `import.meta.env` and the browser globals do not exist.
 */

/**
 * Canonical origin, no trailing slash. Every canonical/OG/sitemap URL is built
 * from this, so it must be the domain you want Google to index — including on
 * preview deploys, which should point back at production rather than compete
 * with it.
 *
 * CHANGE THIS to the production domain before launch, then verify it in Google
 * Search Console.
 */
export const SITE_URL = 'https://oversightinsights.com';

export const SITE = {
  name: 'Oversight Insights',
  /** Used where the full name would wrap awkwardly (icons, manifest). */
  shortName: 'Oversight',
  url: SITE_URL,
  /** Visible strapline. Overridden for display by `site_settings.tagline`. */
  tagline: 'Audit & investigations, explained simply.',
  /**
   * Default meta description. Kept under ~155 characters so Google shows it
   * whole rather than truncating mid-sentence.
   */
  description:
    'Audit and investigations explained in plain language. Real cases, no jargon, no assumed knowledge — one lesson a week, free.',
  lang: 'en',
  locale: 'en_US',
  /** Fallback social card. 1200×630 so it renders large on X/LinkedIn/Slack. */
  ogImage: '/og.png',
  ogImageAlt: 'Oversight Insights — audit & investigations, explained simply.',
  /** Brand accent, mirrored from `--accent` in styles/theme.css. */
  themeColor: '#fbbf24',
  contactEmail: 'hello@oversightinsights.com',
  /** Set to '@handle' once the account exists; empty means "omit the tag". */
  twitterHandle: '',
} as const;

/** Absolute URL for a site-relative path. Absolute inputs pass through. */
export function absoluteUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Document title for a page. The bare site name on the home page; elsewhere
 * `Page · Oversight Insights`, which keeps the distinguishing words at the
 * front where they survive Google's ~60-character truncation.
 */
export function pageTitle(title?: string | null): string {
  const trimmed = title?.trim();
  return trimmed && trimmed !== SITE.name ? `${trimmed} · ${SITE.name}` : SITE.name;
}
